import React, { useContext, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'

import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getCheapestAvailablePackageOption,
  getCheapestSelectedPackageOption,
  getDurationPackageOptions,
  getHasSoloCapacityPackages,
} from 'selectors/offerPage/offerPageSelectors'

import FlashHotelDesktopSameRoomOption from './FlashHotelDesktopSameRoomOption'
import { buildRoomPackageOptionsViewOrder, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { SecureWithDepositBanner } from 'components/Common/SecureWithDeposit/SecureWithDepositBanner'
import { getDepositAmountPercentage, isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import GeoContext from 'contexts/geoContext'
import config from 'constants/config'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import ReserveForZeroPaymentBanner from 'components/Common/ReserveForZeroPaymentBanner/ReserveForZeroPaymentBanner'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import OfferPageFlightDealBanner from '../OfferPageFlightDealBanner'
import useHasGotToAnchor from 'hooks/useHasGotToAnchor'
import moment from 'moment'
import { max } from 'lib/array/arrayUtils'
import TravellingSoloBanner from 'components/Common/TravellingSoloBanner/TravellingSoloBanner'
import { isSoloOccupancy } from 'lib/offer/occupancyUtils'
import ContentTagBanner from 'components/Content/ContentTagBanner'
import Group from '../../utils/Group'

const PackagesLayoutContainer = styled(LayoutContainer)`
  padding: 0 ${rem(16)};
`

interface Props {
  offer: App.HotelOffer
  destinationAirport?: App.Airport
  packageOptions: Array<App.OfferPackageOptionView>
  cheapestSelectedOption?: App.OfferPackageOptionView
  cheapestOption: App.OfferPackageOptionView
  checkIn?: string
  airport?: App.Airport
  isSpoofed: boolean
  defaultDepositAmountPercentage: number
  rooms: Array<App.Occupants>
  hasSoloCapacityPackages: boolean
  onStickyFooterToggle: (visible: boolean) => void
  charterBundleRoute?: App.CharterBundleRoute
}

function FlashHotelDesktopSameRoomOptions(props: Props) {
  const {
    destinationAirport,
    offer,
    packageOptions,
    cheapestOption,
    cheapestSelectedOption,
    checkIn,
    isSpoofed,
    defaultDepositAmountPercentage,
    airport,
    rooms,
    hasSoloCapacityPackages,
    onStickyFooterToggle,
    charterBundleRoute,
  } = props

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)

  const packageOptionsByRoom = useMemo(() => {
    return buildRoomPackageOptionsViewOrder(packageOptions)
  }, [packageOptions])

  const availableRoomCountByRoomId = useMemo(() => {
    const availablePackageOptions = packageOptions.filter(packageOption => (packageOption.availableRooms || 0) > 0)
    const packageView = max(availablePackageOptions, packageView => packageView.package.roomType?.stats.booked || 0)
    if (packageView?.availableRooms && packageView.package.roomType?.id && packageView.availableRooms > 0 && packageView.availableRooms <= 10) {
      return new Map([[packageView.package.roomType?.id, packageView?.availableRooms]])
    }
    return new Map()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageOptions, checkIn])

  const { currentCurrency } = useContext(GeoContext)
  const packageView = cheapestSelectedOption ?? cheapestOption
  const { hotelTotal } = packageView
  const showDealBanner = airport && destinationAirport && !charterBundleRoute
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const showSoloBanner = hasSoloCapacityPackages && !isSoloOccupancy(rooms)
  const depositsAvailable = isDepositAvailableOnOffer(offer, currentCurrency, checkInMoment, hotelTotal)
  const reserveForZeroAvailable = isSpoofed && isPackageEligibleForZeroReservation(offer, packageView.package.roomRate?.isReservableForZero, checkInMoment)
  const packageStartRef = useRef<HTMLDivElement>(null)

  const reachedAnchor = useHasGotToAnchor(packageStartRef?.current, -110, 25)

  const activeBanner = useMemo(() => {
    if (showSoloBanner) {
      return <TravellingSoloBanner />
    }

    if (reserveForZeroAvailable) {
      return <ReserveForZeroPaymentBanner />
    }
    if (depositsAvailable && !reserveForZeroAvailable && config.DEPOSITS_TAGS_ENABLED) {
      return <SecureWithDepositBanner
        offerType={offer.type}
        depositAmountPercentage={depositAmountPercentage}
        depositThresholds={offer.depositThresholds}
        depositAmount={offer.depositAmount}
      />
    }
    if (showDealBanner && config.FLIGHT_ENABLED) {
      return <OfferPageFlightDealBanner
        destinationAirportCode={destinationAirport.code}
        originAirportCode={airport.code}
      />
    }
  }, [airport, depositAmountPercentage, depositsAvailable, destinationAirport, offer, reserveForZeroAvailable, showDealBanner, showSoloBanner])

  useEffect(() => {
    onStickyFooterToggle(!reachedAnchor)
  }, [onStickyFooterToggle, reachedAnchor])

  return (
    <VerticalSpacer gap={16}>
      <LayoutContainer>
        <Group direction="vertical" gap={16}>
          <ContentTagBanner offerId={offer.id} page="offer" />
          {activeBanner}
        </Group>
      </LayoutContainer>

      <PackagesLayoutContainer>
        <VerticalSpacer gap={32} ref={packageStartRef}>
          {packageOptionsByRoom.map((item) =>
            <FlashHotelDesktopSameRoomOption
              key={item.roomId}
              roomTypeId={item.roomId}
              offer={offer}
              packageViews={item.packageViews}
              availableRoomCount={availableRoomCountByRoomId.get(item.roomId)}
            />,
          )}
        </VerticalSpacer>
      </PackagesLayoutContainer>
    </VerticalSpacer>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  cheapestSelectedOption: getCheapestSelectedPackageOption(appState, state),
  cheapestOption: getCheapestAvailablePackageOption(state, appState),
  offer: state.offer!,
  selectedDuration: state.duration,
  checkIn: state.checkInDate,
  airport: state.airport,
  packageOptions: getDurationPackageOptions(state, appState),
  isSpoofed: appState.auth.account.isSpoofed,
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  open: !!state.activeFilter,
  hasSoloCapacityPackages: getHasSoloCapacityPackages(state, appState),
  rooms: state.rooms,
  charterBundleRoute: state.charterBundleRoute,
}), FlashHotelDesktopSameRoomOptions, preventRenderWithOpenFilter)
